import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonNote,
  IonPage,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { addCircleOutline, closeOutline, swapHorizontalOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useZoneBusinesses } from '../../lib/auth/useZoneBusinesses.js';
import { getSupabase } from '../../lib/supabase.js';
import { getSyncEngine } from '../../lib/sync/engine.js';
import { useSyncState } from '../../lib/sync/SyncProvider.js';
import { openExternalFromAsync } from '../../lib/openExternalUrl.js';
import { productionPlanSaveUiAfterConflictRefresh } from '../../lib/productionPlanConflictUi.js';
import { menuSlotForDate, menuSlotLabel, WEEKDAY_LABELS, parseOrdinarioLabel, collettivaDietLabel, mealCompositionForDiet, matchCollettivaDiet, resolveCollettivaDish } from '@trackeat/shared';
import type { RecipeIngredient } from '@trackeat/shared';
import { bluecoopMenuDishes, hasBluecoopMenu } from '../../lib/collettiva/bluecoopMenu.js';

// T-282/T-298 — Pianifica produzione (lato RESPONSABILE), una sola scorsa:
// la sezione "Totali richiesti" è anche dove si ASSEGNA un piatto a ogni riga
// d'ordine. Gli "ordinari" si auto-assegnano dalla rotazione del giorno; le diete
// speciali (diabetico/iposodico/…) le assegna il responsabile (popup con ricerca,
// in base al magazzino). La produzione del giorno (sezione 2) è il risultato
// aggregato per ricetta. Quantità = dall'ordine aggregato. NON tocca lo stock.

function normName(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['’`´]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

interface SRLine {
  meal_service?: string;
  description?: string;
  qty?: number;
  // T-445: campi per l'espansione dieta→piatti lato cucina. `letto` presente =
  // ordine per-LETTO (dieta, da espandere); assente = ordine "Ordini" a catalogo
  // (la description è già un piatto). `menu_label` porta il codice dieta.
  letto?: string | null;
  menu_label?: string | null;
  primo?: { label?: string } | null;
  secondo?: { label?: string } | null;
  contorno?: { label?: string } | null;
  frutta?: { label?: string } | null;
}
interface SRRow {
  destination_id: string;
  lines: SRLine[] | null;
  status?: string;
}
interface CatRow {
  label: string;
  category: string;
  sort_order: number;
}
interface RecipeRow {
  id: string;
  name: string;
  menu_label: string | null;
  // T-441: campi variante. Le basi hanno variant_of_recipe_id/diet_code null;
  // una variante è una ricetta figlia (variant_of_recipe_id = id della base).
  variant_of_recipe_id: string | null;
  diet_code: string | null;
  ingredients?: RecipeIngredient[];
}
type Meal = 'pranzo' | 'cena' | 'colazione';
interface AggRow {
  label: string;
  category: string;
  sort: number;
  total: number;
  byDest: Record<string, number>;
}
interface Assignment {
  recipe_id: string;
  recipe_name: string;
  // T-441: dieta risolta della riga (null = ordinaria/base o match diretto/alias).
  diet_code: string | null;
  // T-441: label famiglia-ordinario con dieta ma variante figlia non ancora
  // definita → si aggancia la base come stima ("da definire" a valle).
  variant_pending: boolean;
}

const CATEGORY_ORDER = ['PRIMI', 'SECONDI', 'CONTORNI', 'FRUTTA', 'ALTRO'] as const;
const MEALS: ReadonlyArray<readonly [Meal, string]> = [
  ['pranzo', 'Pranzo'],
  ['cena', 'Cena'],
  ['colazione', 'Colazione'],
];
const todayIso = (): string => new Date().toISOString().slice(0, 10);

// Auto-assegna le etichette della "famiglia ordinario" del catalogo al piatto di
// rotazione del giorno, risolvendo la DIETA a una ricetta VARIANTE quando esiste
// (T-441). parseOrdinarioLabel estrae (categoria, dietCode): dietCode=null →
// ordinario base; dietCode valorizzato → si cerca la ricetta figlia
// (variant_of_recipe_id === base.id && diet_code === dietCode). Se la variante
// non è definita, fallback sulla base marcato variant_pending (i consumatori a
// valle mostrano "da definire" e stimano sul BOM base).
// T-447: la risoluzione dieta→ricetta (base/variante/pending) vive ORA nel resolver
// UNICO condiviso `resolveCollettivaDish` (packages/shared) — stessa logica, niente
// più duplicati (usato anche dal DDT). Qui resta solo il gate "famiglia ordinario"
// (le altre label → direct-match/alias sotto) + la rotazione del giorno per categoria.
function autoAssign(aggList: AggRow[], recipes: RecipeRow[], serviceDate: string, meal: Meal): Record<string, Assignment> {
  const out: Record<string, Assignment> = {};
  if (!hasBluecoopMenu(meal)) return out;
  const slot = menuSlotForDate(serviceDate);
  const dishes = bluecoopMenuDishes(slot, slot.weekday, meal === 'cena' ? 'cena' : 'pranzo');
  if (dishes.length === 0) return out;
  const rotationByCategory: Record<string, string> = {};
  for (const d of dishes) rotationByCategory[d.category] = d.name;
  for (const a of aggList) {
    // Gate: qui SOLO la famiglia ordinario; le altre label → direct-match/alias (sotto).
    if (!parseOrdinarioLabel(a.label)) continue;
    const resolved = resolveCollettivaDish(a.label, { recipes, rotationByCategory, dietLabel: collettivaDietLabel });
    if (resolved) out[a.label] = resolved;
  }
  return out;
}

export default function ProductionPlanPage(): JSX.Element {
  const { profile } = useAuth();
  // T-403: zona della sede attiva (null = tenant-wide, vedi useZoneBusinesses).
  const { zoneIds } = useZoneBusinesses();
  const queryClient = useQueryClient();
  const syncState = useSyncState();
  const sb = useMemo(() => getSupabase(), []);

  const [serviceDate, setServiceDate] = useState<string>(todayIso());
  const [meal, setMeal] = useState<Meal>('pranzo');
  const [loading, setLoading] = useState(true);
  const [destNames, setDestNames] = useState<Record<string, string>>({});
  const [agg, setAgg] = useState<AggRow[]>([]);
  const [catMap, setCatMap] = useState<Record<string, { category: string; sort: number }>>({});
  const [recipes, setRecipes] = useState<RecipeRow[]>([]);
  const [assignments, setAssignments] = useState<Record<string, Assignment>>({});
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [picker, setPicker] = useState<{ label: string; category: string } | null>(null);
  const [search, setSearch] = useState('');
  // T-406 (A9): piatti aggiunti a MANO al piano (fuori dagli ordini aggregati).
  const [manualDishes, setManualDishes] = useState<{ recipe_id: string; recipe_name: string; category: string; portions: number }[]>([]);
  const [saving, setSaving] = useState(false);
  const [planConflict, setPlanConflict] = useState(false);
  // T-349 (audit UX): dopo il salvataggio si mostra il passo successivo
  // (Comanda di produzione, step 3) — prima il flusso si interrompeva qui.
  const [justSaved, setJustSaved] = useState(false);
  // Richieste 'submitted' (da accettare) per il giorno/servizio: guida
  // l'empty-state quando il piano è vuoto ma gli ordini sono arrivati.
  const [submittedCount, setSubmittedCount] = useState(0);
  const history = useHistory();
  const [toast, setToast] = useState<string | null>(null);
  // T-304: export PDF (matrice piatti×destinazioni per il carico + totali cucina).
  const [exporting, setExporting] = useState<null | 'zone' | 'totali'>(null);
  const [readyPdf, setReadyPdf] = useState<{ label: string; url: string } | null>(null);
  // T-406 (A8): alias persistente catalogo(label normalizzata) → ricetta, così i
  // piatti NON ordinari si auto-assegnano da soli dopo la prima assegnazione.
  const dishAliasRef = useRef<Record<string, Assignment>>({});
  const [aliasVer, setAliasVer] = useState(0);

  // Anagrafiche stabili (catalogo categorie, ricette, business) una volta.
  useEffect(() => {
    let mounted = true;
    void (async () => {
      const [cat, rec, biz, tn] = await Promise.all([
        sb.from('order_catalog_rows').select('label,category,sort_order'),
        sb.from('recipes').select('id,name,menu_label,variant_of_recipe_id,diet_code,ingredients').is('archived_at', null).order('name'),
        profile?.active_business_id
          ? Promise.resolve({ data: [{ id: profile.active_business_id }] })
          // T-403: il fallback non deve pescare una sede ARCHIVIATA (es. la
          // ex-sede amministrativa Bluecoop) — filtro + ordine deterministico.
          : sb.from('businesses').select('id').is('archived_at', null).order('created_at', { ascending: true }).limit(1),
        profile?.active_tenant_id ? sb.from('tenants').select('settings').eq('id', profile.active_tenant_id).single() : Promise.resolve({ data: null }),
      ]);
      if (!mounted) return;
      const cm: Record<string, { category: string; sort: number }> = {};
      for (const c of (cat.data as CatRow[] | null) ?? []) cm[c.label] = { category: c.category, sort: c.sort_order };
      setCatMap(cm);
      setRecipes((rec.data as RecipeRow[] | null) ?? []);
      setBusinessId(((biz.data as Array<{ id: string }> | null) ?? [])[0]?.id ?? null);
      const alias = ((tn.data as { settings?: { collettiva_dish_recipe_alias?: Record<string, Assignment> } } | null)?.settings?.collettiva_dish_recipe_alias) ?? {};
      dishAliasRef.current = alias;
      setAliasVer((v) => v + 1);
    })();
    return () => {
      mounted = false;
    };
  }, [sb, profile]);

  // Salva l'alias label→ricetta (best-effort, non blocca il flusso).
  const persistAlias = useCallback(async (label: string, asg: Assignment) => {
    const tenantId = profile?.active_tenant_id;
    if (!tenantId) return;
    const key = normName(label);
    dishAliasRef.current = { ...dishAliasRef.current, [key]: asg };
    try {
      const { data } = await sb.from('tenants').select('settings').eq('id', tenantId).single();
      const settings = { ...((data as { settings?: Record<string, unknown> } | null)?.settings ?? {}), collettiva_dish_recipe_alias: dishAliasRef.current };
      await sb.from('tenants').update({ settings }).eq('id', tenantId);
    } catch { /* best-effort */ }
  }, [sb, profile]);

  // Aggrega le richieste del giorno/servizio + auto-assegna gli ordinari.
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    void (async () => {
      // T-349 (audit): si pianifica SOLO ciò che il responsabile ha ACCETTATO
      // (prima sommava anche le 'submitted', rendendo inutile lo step Accetta).
      // Le 'submitted' si scaricano comunque per l'empty-state onesto sotto.
      // T-379: escludi gli ordini ARCHIVIATI (ritirati dal preposto / doppioni
      // bonificati): prima venivano comunque sommati nel piano.
      // T-403: scope di ZONA — con più zone (Favara/Sciacca) si aggregano solo
      // gli ordini e le destinazioni delle sedi della zona attiva; senza città
      // configurata zoneIds è null → tenant-wide come prima.
      let srQ = sb.from('supply_requests').select('destination_id,lines,status').eq('requested_for_date', serviceDate).in('status', ['submitted', 'accepted']).is('archived_at', null);
      let dstQ = sb.from('destinations').select('id,name');
      if (zoneIds) {
        srQ = srQ.in('business_id', zoneIds);
        dstQ = dstQ.in('business_id', zoneIds);
      }
      const [sr, dst] = await Promise.all([srQ, dstQ]);
      if (!mounted) return;
      const names: Record<string, string> = {};
      for (const d of (dst.data as Array<{ id: string; name: string }> | null) ?? []) names[d.id] = d.name;
      setDestNames(names);
      const acc: Record<string, AggRow> = {};
      // T-445: lista label di catalogo per il filtro di mealCompositionForDiet
      // (uno slot il cui piatto non è a catalogo degrada a null).
      const catalogLite = Object.keys(catMap).map((label) => ({ label }));
      // Richieste solo INVIATE (da accettare) col servizio selezionato: non
      // entrano nel piano ma guidano l'empty-state (vicolo cieco → step 1).
      let submittedN = 0;
      for (const row of (sr.data as SRRow[] | null) ?? []) {
        if (row.status === 'submitted' && (row.lines ?? []).some((ln) => (ln.meal_service ?? 'pranzo') === meal)) {
          submittedN += 1;
        }
        // T-429: i piatti ordinati entrano nel TOTALE anche se la richiesta è
        // solo 'submitted' (non ancora accettata), così il centro cottura vede
        // subito cosa è stato ordinato. (Prima le submitted venivano saltate.)
        for (const ln of row.lines ?? []) {
          if ((ln.meal_service ?? 'pranzo') !== meal) continue;
          const q = Number(ln.qty) || 0;
          if (q <= 0) continue;
          // Aggrega q pasti su una label-piatto (byDest per il DDT/carrello reparto).
          const addDish = (dishLabel: string | null | undefined): void => {
            const lab = (dishLabel ?? '').trim();
            if (!lab) return;
            const meta = catMap[lab] ?? { category: 'ALTRO', sort: 9999 };
            const a = (acc[lab] ??= { label: lab, category: meta.category, sort: meta.sort, total: 0, byDest: {} });
            a.total += q;
            a.byDest[row.destination_id] = (a.byDest[row.destination_id] ?? 0) + q;
          };
          // T-445 — ESPANSIONE dieta→piatti: una riga per-LETTO è un ordine DIETA
          // (es. "Diabetica"), che la cucina espande nei suoi piatti per portata
          // (primo/secondo/contorno/frutta) — usando il trittico messo dal preposto
          // sulla riga; dove uno slot manca, la composizione auto-accoppiata dal
          // codice dieta (mealCompositionForDiet, tabella reale dell'Excel). Una riga
          // SENZA letto è un ordine "Ordini" a catalogo: la `description` è già un
          // piatto → invariata (nessuna espansione, nessun doppio conteggio).
          const isBedDiet = String(ln.letto ?? '').trim() !== '';
          if (isBedDiet) {
            const dietCode = String(ln.menu_label ?? '').trim() || matchCollettivaDiet(ln.description ?? '');
            const autoComp = mealCompositionForDiet(dietCode, catalogLite);
            const dishes = [
              ln.primo?.label ?? autoComp.primo,
              ln.secondo?.label ?? autoComp.secondo,
              ln.contorno?.label ?? autoComp.contorno,
              ln.frutta?.label ?? autoComp.frutta,
            ].map((x) => (x ?? '').trim()).filter((x) => x !== '');
            if (dishes.length > 0) {
              for (const d of dishes) addDish(d);
            } else {
              // T-445 (fix verifica): dieta SENZA composizione né trittico
              // (es. Ipocalorica, Personalizzata) → mostra la DIETA come riga
              // visibile (da comporre a mano), MAI farla sparire dalla cucina.
              addDish(ln.description);
            }
          } else {
            addDish(ln.description);
          }
        }
      }
      const list = Object.values(acc).sort(
        (x, y) => CATEGORY_ORDER.indexOf(x.category as (typeof CATEGORY_ORDER)[number]) - CATEGORY_ORDER.indexOf(y.category as (typeof CATEGORY_ORDER)[number]) || x.sort - y.sort,
      );
      setAgg(list);
      setSubmittedCount(submittedN);
      // T-431: match DIRETTO label→ricetta (per nome normalizzato, e per
      // menu_label) come BASE per TUTTI i piatti — non solo gli ordinari. Es.
      // "Pasta al sugo" → ricetta "Pasta al sugo", "Patate lesse", ecc. (T-420 ha
      // caricato 46 ricette col nome giusto). Precedenza finale (chi vince):
      // rotazione ordinari del giorno > alias salvato dall'utente > match diretto.
      // T-441: il match diretto per nome usa SOLO le basi — una variante (ricetta
      // figlia, il cui nome contiene la dieta) non deve vincere il match per nome.
      const byNorm = new Map<string, RecipeRow>();
      for (const r of recipes) {
        if (r.variant_of_recipe_id != null) continue;
        byNorm.set(normName(r.name), r);
        if (r.menu_label && !byNorm.has(normName(r.menu_label))) byNorm.set(normName(r.menu_label), r);
      }
      const fromDirect: Record<string, Assignment> = {};
      for (const a of list) {
        const rec = byNorm.get(normName(a.label));
        if (rec) fromDirect[a.label] = { recipe_id: rec.id, recipe_name: rec.name, diet_code: null, variant_pending: false };
      }
      // T-406 (A8): applica gli alias salvati (piatto→ricetta); la rotazione
      // ordinari VINCE sugli alias (assegnazione del giorno più fresca).
      const auto = autoAssign(list, recipes, serviceDate, meal);
      const fromAlias: Record<string, Assignment> = {};
      for (const a of list) {
        const al = dishAliasRef.current[normName(a.label)];
        // T-441: gli alias sono associazioni manuali label→ricetta → sempre
        // diet_code=null/variant_pending=false (normalizza anche gli alias legacy
        // salvati prima di T-441, che non portavano questi campi).
        if (al && recipes.some((r) => r.id === al.recipe_id)) {
          fromAlias[a.label] = { recipe_id: al.recipe_id, recipe_name: al.recipe_name, diet_code: null, variant_pending: false };
        }
      }
      setAssignments({ ...fromDirect, ...fromAlias, ...auto });
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [sb, serviceDate, meal, catMap, recipes, zoneIds, aliasVer]);

  // T-406: "già salvato" si azzera solo cambiando giorno/servizio (non a ogni
  // reload di ricette/catalogo) → il pulsante "Vai alla Comanda" resta dopo il
  // salvataggio e "Salva" torna attivo solo su un vero cambio.
  useEffect(() => { setJustSaved(false); setManualDishes([]); }, [serviceDate, meal]);

  useEffect(() => {
    let active = true;
    if (!profile?.active_tenant_id || !businessId) {
      setPlanConflict(false);
      return () => { active = false; };
    }
    void getSyncEngine().latestProductionPlanConflict({
      tenantId: profile.active_tenant_id,
      businessId,
      serviceDate,
      mealService: meal,
    }).then((conflict) => {
      if (!active) return;
      const hasConflict = conflict !== null;
      setPlanConflict(hasConflict);
      // A terminal rejection happens asynchronously after the durable local
      // enqueue. It must revoke the provisional "saved" UI without requiring a
      // remount/date change.
      setJustSaved((current) => (
        productionPlanSaveUiAfterConflictRefresh(
          current,
          hasConflict,
        ).justSaved
      ));
    }).catch(() => undefined);
    return () => { active = false; };
  }, [
    profile?.active_tenant_id,
    businessId,
    serviceDate,
    meal,
    syncState.status,
    syncState.lastPushAt,
    syncState.lastPullAt,
    syncState.lastError,
    syncState.pendingCount,
  ]);

  const grouped = useMemo(() => {
    const m: Record<string, AggRow[]> = {};
    for (const a of agg) (m[a.category] ??= []).push(a);
    return m;
  }, [agg]);
  const totalRequested = useMemo(() => agg.reduce((s, a) => s + a.total, 0), [agg]);
  const slot = useMemo(() => menuSlotForDate(serviceDate), [serviceDate]);

  // Produzione del giorno = aggregata per ricetta assegnata.
  // T-441: la chiave fonde per (recipe_id, diet_code) — base e variante dello
  // stesso recipe_id (caso fallback "pending", dove recipe_id resta quello della
  // base) restano righe DISTINTE; le varianti definite hanno già un recipe_id
  // proprio (la ricetta figlia).
  const planByRecipe = useMemo(() => {
    const m = new Map<string, { recipe_id: string; recipe_name: string; category: string; portions: number; diet_code: string | null; variant_pending: boolean }>();
    for (const a of agg) {
      const asg = assignments[a.label];
      if (!asg) continue;
      const key = `${asg.recipe_id}::${asg.diet_code ?? ''}`;
      const cur = m.get(key);
      if (cur) cur.portions += a.total;
      else m.set(key, { recipe_id: asg.recipe_id, recipe_name: asg.recipe_name, category: a.category, portions: a.total, diet_code: asg.diet_code, variant_pending: asg.variant_pending });
    }
    // T-406 (A9): somma i piatti aggiunti a mano (portions > 0). Sempre base
    // (diet_code=null/variant_pending=false).
    for (const md of manualDishes) {
      if (md.portions <= 0) continue;
      const key = `${md.recipe_id}::`;
      const cur = m.get(key);
      if (cur) cur.portions += md.portions;
      else m.set(key, { ...md, diet_code: null, variant_pending: false });
    }
    return [...m.values()].sort((x, y) => CATEGORY_ORDER.indexOf(x.category as (typeof CATEGORY_ORDER)[number]) - CATEGORY_ORDER.indexOf(y.category as (typeof CATEGORY_ORDER)[number]));
  }, [agg, assignments, manualDishes]);
  const nUnassigned = useMemo(() => agg.filter((a) => !assignments[a.label]).length, [agg, assignments]);

  const pickRecipe = useCallback(
    (rec: RecipeRow) => {
      if (!picker) return;
      // T-406 (A9): modalità "aggiungi piatto libero" al piano.
      if (picker.label === '__manual__') {
        setManualDishes((p) => (p.some((d) => d.recipe_id === rec.id) ? p : [...p, { recipe_id: rec.id, recipe_name: rec.name, category: 'ALTRO', portions: 0 }]));
        setJustSaved(false);
        setPicker(null);
        setSearch('');
        return;
      }
      // T-441: assegnazione manuale = ricetta scelta a mano → nessuna dieta risolta.
      const asg: Assignment = { recipe_id: rec.id, recipe_name: rec.name, diet_code: null, variant_pending: false };
      setAssignments((p) => ({ ...p, [picker.label]: asg }));
      // T-406 (A8): memorizza l'associazione per auto-assegnarla la prossima volta.
      void persistAlias(picker.label, asg);
      setJustSaved(false);
      setPicker(null);
      setSearch('');
    },
    [picker, persistAlias],
  );
  const clearAssign = useCallback((label: string) => {
    setJustSaved(false);
    setAssignments((p) => {
      const next = { ...p };
      delete next[label];
      return next;
    });
  }, []);

  const filteredRecipes = useMemo(() => {
    const q = normName(search);
    // T-441: il picker manuale sceglie un PIATTO base — le varianti dieta (ricette
    // figlie) non devono comparire come selezionabili (si agganciano da sole).
    const bases = recipes.filter((r) => r.variant_of_recipe_id == null);
    const list = q ? bases.filter((r) => normName(r.name).includes(q)) : bases;
    // Suggerimento di rotazione della categoria in cima.
    if (picker && hasBluecoopMenu(meal)) {
      const dishes = bluecoopMenuDishes(slot, slot.weekday, meal === 'cena' ? 'cena' : 'pranzo');
      const dishName = dishes.find((d) => d.category === picker.category)?.name;
      if (dishName) {
        const sug = normName(dishName);
        return [...list].sort((a, b) => (normName(a.name) === sug ? -1 : 0) - (normName(b.name) === sug ? -1 : 0));
      }
    }
    return list;
  }, [search, recipes, picker, meal, slot]);

  const save = useCallback(async () => {
    if (!profile?.active_tenant_id || !businessId) {
      setToast('Manca tenant/centro cottura attivo.');
      return;
    }
    if (planByRecipe.length === 0) {
      setToast('Assegna almeno un piatto agli ordini.');
      return;
    }
    setSaving(true);
    try {
      const wasOffline = typeof navigator !== 'undefined' && !navigator.onLine;
      // T-481: envelope, snapshot, piano e productions ottimistiche entrano
      // nella stessa transazione SQLite PRIMA di qualunque rete. Il dispatcher
      // conserva request_id/ID riga/trace token attraverso crash e lost response.
      await getSyncEngine().queueProductionPlanReplacement({
        tenantId: profile.active_tenant_id,
        businessId,
        actorUserId: profile.id,
        serviceDate,
        mealService: meal,
        items: planByRecipe.map((row) => ({
          recipe_id: row.recipe_id,
          output_name: row.recipe_name,
          category: row.category as
            | 'PRIMI'
            | 'SECONDI'
            | 'CONTORNI'
            | 'FRUTTA'
            | 'ALTRO',
          planned_portions: row.portions,
          diet_code: row.diet_code ?? null,
        })),
      });
      await queryClient.invalidateQueries({ queryKey: ['masters'] });
      setPlanConflict(false);
      setJustSaved(true);
      setToast(
        wasOffline
          ? 'Piano salvato offline sul dispositivo: sarà sincronizzato nello stesso ordine delle operazioni di magazzino.'
          : 'Piano salvato sul dispositivo e in sincronizzazione atomica.',
      );
      return;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      if (
        message.includes('production_plan_replace_operational_production_v1')
        || message.includes('production_plan_replace_completed_plan_v1')
      ) {
        setToast('Piano non sostituito: una produzione ha già scarichi, tracciabilità, etichette o registrazioni operative. Il piano e la produzione esistenti sono rimasti invariati; crea un nuovo piano o usa la rettifica.');
      } else if (message.includes('production_plan_replace_busy_retry_v1')) {
        setToast('Un’altra operazione di magazzino o produzione è in corso: nessuna modifica applicata. Attendi e riprova.');
      } else if (
        message.includes('atomic_rpc_scope_conflict_requires_resolution')
      ) {
        setPlanConflict(true);
        setToast('Questo piano ha un conflitto server da riconciliare prima di un nuovo salvataggio.');
      } else {
        setToast(`Errore salvataggio: ${message}`);
      }
    } finally {
      setSaving(false);
    }
  }, [queryClient, planByRecipe, profile, businessId, serviceDate, meal]);

  const resolvePlanConflict = useCallback(async () => {
    if (!profile?.active_tenant_id || !businessId) return;
    setSaving(true);
    try {
      await getSyncEngine().resolveProductionPlanConflict({
        tenantId: profile.active_tenant_id,
        businessId,
        serviceDate,
        mealService: meal,
      });
      await queryClient.invalidateQueries({ queryKey: ['masters'] });
      setPlanConflict(false);
      setJustSaved(false);
      setToast('Verità server adottata. Puoi controllare il piano e salvarlo di nuovo.');
    } catch (error) {
      setToast(`Riconciliazione non riuscita: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setSaving(false);
    }
  }, [profile, businessId, serviceDate, meal, queryClient]);

  // T-304: genera un PDF dalle richieste della giornata. aggregate=false →
  // matrice piatti×destinazioni (per chi carica i camion); aggregate=true →
  // totali per piatto senza destinazioni (per gli chef).
  const exportPiattiZona = useCallback(
    async (aggregate: boolean): Promise<void> => {
      if (!profile?.active_tenant_id) { setToast('Manca tenant attivo.'); return; }
      setReadyPdf(null);
      setExporting(aggregate ? 'totali' : 'zone');
      const label = aggregate ? 'Totali cucina' : 'Lista per destinazioni';
      // Cucina (totali ricette + ingredienti) vs matrice per destinazione.
      const fn = aggregate ? 'export-produzione-cucina' : 'export-piatti-zona';
      // T-403: scope di ZONA per entrambe le stampe (business_ids = sedi della
      // zona attiva). Con zona non configurata si ricade sul comportamento
      // T-379: matrice per destinazioni tenant-wide (il filtro sulla singola
      // sede attiva azzerava il PDF a Bluecoop), totali cucina sulla sede
      // attiva (i production_plan_items vivono lì).
      // T-445: per la "Lista per destinazioni" passa la mappa piatto(label)→ricetta
      // ASSEGNATA (risolta qui in Pianifica, varianti ordinario incluse) così il PDF
      // scrive il NOME RICETTA reale ("Pasta al sugo") invece della label astratta
      // ("1° Ordinario diabetico"). L'edge da sola non saprebbe risolvere le varianti.
      const dishRecipes: Record<string, string> = {};
      if (!aggregate) {
        for (const [lbl, a] of Object.entries(assignments)) {
          if (a?.recipe_name) dishRecipes[lbl] = a.recipe_name;
        }
      }
      try {
        const res = await openExternalFromAsync(async () => {
          const { data, error } = await sb.functions.invoke<{ pdf_url?: string }>(fn, {
            body: {
              tenant_id: profile.active_tenant_id,
              service_date: serviceDate,
              meal_service: meal,
              business_ids: zoneIds ?? undefined,
              business_id: !zoneIds && aggregate ? (businessId ?? undefined) : undefined,
              dish_recipes: aggregate ? undefined : dishRecipes,
            },
          });
          if (error) throw error;
          if (!data?.pdf_url) throw new Error('Nessun PDF restituito');
          return data.pdf_url;
        });
        // T-406: mostra il tasto "Apri PDF" SOLO se l'apertura automatica è stata
        // bloccata dal browser (fallback). Se il PDF si è già aperto, niente tasto.
        if (res.url && res.blocked) setReadyPdf({ label, url: res.url });
        setToast(res.blocked ? `${label}: pronto — tocca "Apri PDF"` : `${label}: PDF aperto`);
      } catch (e) {
        setToast(`Errore export: ${e instanceof Error ? e.message : String(e)}`);
      } finally {
        setExporting(null);
      }
    },
    [sb, profile, serviceDate, meal, businessId, zoneIds, assignments],
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons>
          <IonTitle>Pianifica produzione</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonList inset>
          <IonItem>
            <IonLabel position="stacked">Giorno di produzione</IonLabel>
            <IonInput type="date" value={serviceDate} onIonInput={(e) => setServiceDate(String(e.detail.value ?? todayIso()))} />
          </IonItem>
          <IonItem>
            <IonLabel>Servizio</IonLabel>
            <IonSelect value={meal} interface="popover" onIonChange={(e) => setMeal(e.detail.value as Meal)}>
              {MEALS.map(([v, l]) => (
                <IonSelectOption key={v} value={v}>{l}</IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>
        </IonList>

        {hasBluecoopMenu(meal) && (
          <div style={{ margin: '0 16px 8px', padding: '8px 12px', background: '#dcfce7', borderRadius: 10, fontSize: 13, color: '#15803d', fontWeight: 600 }}>
            🗓️ {menuSlotLabel(slot)} · {WEEKDAY_LABELS[slot.weekday - 1]} — gli ordinari sono già assegnati alla rotazione
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><IonSpinner /></div>
        ) : (
          <>
            <IonText><h2 style={{ margin: '8px 4px 0' }}>1 · Ordini → assegna piatto ({agg.length} piatti · {totalRequested} porzioni)</h2></IonText>
            <IonNote style={{ padding: '0 16px' }}>Tap su una riga per scegliere la ricetta. {nUnassigned > 0 ? `${nUnassigned} righe da assegnare.` : 'Tutte assegnate ✓'}</IonNote>
            {agg.length === 0 ? (
              submittedCount > 0 ? (
                <div style={{ margin: '8px 16px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 12, padding: '10px 14px' }}>
                  <div style={{ fontSize: 13, color: '#92400e' }}>
                    Ci sono <strong>{submittedCount} richieste inviate</strong> per questo giorno/servizio in attesa di
                    accettazione: si pianifica solo ciò che è stato <strong>accettato</strong>.
                  </div>
                  <IonButton size="small" fill="outline" color="warning" style={{ marginTop: 6 }} onClick={() => history.push('/warehouse/supply-requests')}>
                    Vai a Richieste fornitura →
                  </IonButton>
                </div>
              ) : (
                <IonNote style={{ padding: '8px 16px', display: 'block' }}>Nessuna richiesta per questo giorno/servizio.</IonNote>
              )
            ) : (
              CATEGORY_ORDER.filter((c) => (grouped[c] ?? []).length > 0).map((c) => (
                <IonList key={c} inset>
                  <IonListHeader><IonLabel>{c}</IonLabel></IonListHeader>
                  {(grouped[c] ?? []).map((a) => {
                    const asg = assignments[a.label];
                    return (
                      <IonItem key={a.label} button detail={false} onClick={() => { setSearch(''); setPicker({ label: a.label, category: a.category }); }}>
                        <IonLabel>
                          {a.label}
                          <p style={{ fontSize: 12 }}>
                            {Object.entries(a.byDest).map(([id, q]) => `${(destNames[id] ?? '—').slice(0, 14)}: ${q}`).join(' · ')}
                          </p>
                          {asg ? (
                            <p style={{ fontSize: 13, color: '#15803d', fontWeight: 600, marginTop: 2 }}>
                              <IonIcon icon={swapHorizontalOutline} style={{ verticalAlign: '-2px' }} /> {asg.recipe_name}
                            </p>
                          ) : (
                            <p style={{ fontSize: 13, color: '#b45309', fontWeight: 600, marginTop: 2 }}>
                              <IonIcon icon={addCircleOutline} style={{ verticalAlign: '-2px' }} /> Assegna piatto
                            </p>
                          )}
                        </IonLabel>
                        <div slot="end" style={{ textAlign: 'right' }}>
                          <IonNote style={{ fontWeight: 700, fontSize: 18 }}>{a.total}</IonNote>
                          {asg && (
                            <IonButton fill="clear" size="small" color="medium" onClick={(e) => { e.stopPropagation(); clearAssign(a.label); }}>✕</IonButton>
                          )}
                        </div>
                      </IonItem>
                    );
                  })}
                </IonList>
              ))
            )}

            <IonText><h2 style={{ margin: '16px 4px 0' }}>2 · Produzione del giorno</h2></IonText>
            <IonNote style={{ padding: '0 16px' }}>Risultato: i piatti da produrre con le porzioni (somma degli ordini assegnati).</IonNote>
            {planByRecipe.length === 0 ? (
              <IonNote style={{ padding: '8px 16px', display: 'block', fontStyle: 'italic' }}>Assegna i piatti nella sezione 1 per popolare la produzione.</IonNote>
            ) : (
              <IonList inset>
                {planByRecipe.map((r) => (
                  <IonItem key={`${r.recipe_id}::${r.diet_code ?? ''}`}>
                    <IonLabel>{r.recipe_name}<p style={{ fontSize: 12 }}>{r.category}</p></IonLabel>
                    <IonBadge slot="end" color="success" style={{ fontSize: 15 }}>{r.portions}</IonBadge>
                  </IonItem>
                ))}
              </IonList>
            )}
            {/* T-406 (A9): piatti aggiunti a mano (fuori dagli ordini) */}
            {manualDishes.length > 0 && (
              <IonList inset>
                {manualDishes.map((d, i) => (
                  <IonItem key={d.recipe_id}>
                    <IonLabel>{d.recipe_name}<p style={{ fontSize: 12 }}>aggiunto a mano</p></IonLabel>
                    <IonInput
                      slot="end" type="number" inputMode="numeric" placeholder="porz."
                      style={{ maxWidth: 80, textAlign: 'right' } as React.CSSProperties}
                      value={d.portions || ''}
                      onIonInput={(e) => {
                        const v = Math.max(0, Math.round(Number(e.detail.value ?? '') || 0));
                        setJustSaved(false);
                        setManualDishes((p) => p.map((x, j) => (j === i ? { ...x, portions: v } : x)));
                      }}
                    />
                    <IonButton slot="end" fill="clear" color="medium" onClick={() => { setJustSaved(false); setManualDishes((p) => p.filter((_, j) => j !== i)); }}>✕</IonButton>
                  </IonItem>
                ))}
              </IonList>
            )}
            <IonButton expand="block" fill="outline" size="small" style={{ margin: '6px 0 0' }} onClick={() => { setSearch(''); setPicker({ label: '__manual__', category: '' }); }}>
              <IonIcon icon={addCircleOutline} slot="start" /> Aggiungi piatto (fuori ordine)
            </IonButton>
            <IonButton expand="block" disabled={saving || planByRecipe.length === 0 || justSaved || planConflict} onClick={() => void save()} style={{ marginTop: 8 }}>
              {saving ? 'Salvataggio…' : justSaved ? '✓ Salvato' : `Salva produzione del giorno (${planByRecipe.length} piatti)`}
            </IonButton>
            {planConflict && (
              <>
                <IonNote
                  color="danger"
                  style={{ display: 'block', padding: '8px 12px 0' }}
                >
                  Il server ha rifiutato la sostituzione atomica. Le modifiche
                  ottimistiche sono state ripristinate e gli eventuali comandi
                  di magazzino dipendenti sono in quarantena.
                </IonNote>
                <IonButton
                  expand="block"
                  fill="outline"
                  color="danger"
                  disabled={saving}
                  onClick={() => void resolvePlanConflict()}
                >
                  Adotta i dati server e abilita un nuovo salvataggio
                </IonButton>
              </>
            )}
            {justSaved && (
              <IonButton expand="block" fill="outline" style={{ marginTop: 8 }} onClick={() => history.push('/warehouse/production-todo')}>
                Vai alla Comanda di produzione →
              </IonButton>
            )}

            {/* T-304: stampe PDF della giornata (dalle richieste delle destinazioni) */}
            <IonText><h2 style={{ margin: '20px 4px 0' }}>3 · Stampa liste di produzione</h2></IonText>
            <IonNote style={{ padding: '0 16px', display: 'block', marginBottom: 6 }}>
              Dalla data e servizio scelti sopra. I PDF aggregano gli ordini inviati dalle destinazioni.
            </IonNote>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 16px' }}>
              <IonButton expand="block" fill="outline" disabled={exporting !== null} onClick={() => void exportPiattiZona(true)}>
                {exporting === 'totali' ? <IonSpinner name="dots" /> : '👨‍🍳 Totali cucina (senza destinazioni)'}
              </IonButton>
              <IonButton expand="block" fill="outline" disabled={exporting !== null} onClick={() => void exportPiattiZona(false)}>
                {exporting === 'zone' ? <IonSpinner name="dots" /> : '🚚 Lista per destinazioni (carico)'}
              </IonButton>
              {readyPdf && (
                <IonButton expand="block" onClick={() => window.open(readyPdf.url, '_blank', 'noopener,noreferrer')}>
                  Apri PDF — {readyPdf.label}
                </IonButton>
              )}
            </div>
          </>
        )}

        {/* Popup scelta ricetta con ricerca */}
        <IonModal isOpen={!!picker} onDidDismiss={() => setPicker(null)}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start"><IonButton onClick={() => setPicker(null)}><IonIcon icon={closeOutline} /></IonButton></IonButtons>
              <IonTitle>{picker?.label ?? 'Scegli piatto'}</IonTitle>
            </IonToolbar>
            <IonToolbar>
              <IonSearchbar value={search} placeholder="Cerca ricetta…" onIonInput={(e) => setSearch(String(e.detail.value ?? ''))} />
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <IonList>
              {filteredRecipes.map((rec) => (
                <IonItem key={rec.id} button onClick={() => pickRecipe(rec)}>
                  <IonLabel>{rec.name}</IonLabel>
                  {rec.menu_label && <IonBadge slot="end" color="medium">menù {rec.menu_label}</IonBadge>}
                </IonItem>
              ))}
              {filteredRecipes.length === 0 && <IonNote style={{ padding: 16, display: 'block' }}>Nessuna ricetta trovata.</IonNote>}
            </IonList>
          </IonContent>
        </IonModal>

        <IonToast isOpen={toast !== null} message={toast ?? ''} duration={2500} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
